export type OnboardingStepId =
  | "timetable-tap-monday"
  | "timetable-tap-monday-reminder"
  | "timetable-drag-monday"
  | "timetable-drag-monday-reminder"
  | "timetable-click-lesson"
  | "scheme-add-title"
  | "scheme-add-prep"
  | "scheme-nav-teach"
  | "teach-intro"
  | "teach-highlight-panels"
  | "teach-tick-prep"
  | "teach-complete-lesson"
  | "teach-all-set";

export type OnboardingPlacement =
  | "bottom"
  | "top"
  | "left"
  | "right"
  | "bottom-start"
  | "bottom-end"
  | "top-start"
  | "top-end"
  | "right-start"
  | "right-end"
  | "left-start"
  | "left-end";

export type OnboardingStep = {
  id: OnboardingStepId;
  /** Primary element for the tip anchor (data-onboarding-id) */
  targetId: string;
  /** Extra elements to highlight alongside the primary target */
  extraTargetIds?: string[];
  title: string;
  body: string;
  /** Preferred path; coachmark still shows if target exists elsewhere */
  pathPrefix?: string;
  /** Show a Next button (informational steps) */
  showNext?: boolean;
  /** Placement preference relative to the target */
  placement?: OnboardingPlacement;
  /** Override placement on phone-sized viewports (≤600px). */
  mobilePlacement?: OnboardingPlacement;
  /** Override placement on desktop and tablet. */
  desktopPlacement?: OnboardingPlacement;
  /** Full dim overlay, tip fixed at top; no spotlight (finale) */
  fullOverlay?: boolean;
  /** Shown when needed; excluded from step counter and progress */
  unnumbered?: boolean;
  /** Optional follow-up ideas with emoji bullets */
  bullets?: { emoji: string; before: string; emphasis: string; after: string }[];
};

export function resolveStepPlacement(step: OnboardingStep, mobile: boolean): OnboardingPlacement {
  if (mobile && step.mobilePlacement) return step.mobilePlacement;
  if (!mobile && step.desktopPlacement) return step.desktopPlacement;
  return step.placement ?? "bottom";
}

const TIMETABLE_TAP_MONDAY: OnboardingStep = {
  id: "timetable-tap-monday",
  targetId: "timetable-monday",
  pathPrefix: "/timetable",
  title: "Add a Maths lesson",
  body: "Tap on Monday on the timetable and add a Maths lesson.",
  placement: "bottom",
};

const TIMETABLE_TAP_MONDAY_REMINDER: OnboardingStep = {
  id: "timetable-tap-monday-reminder",
  targetId: "timetable-monday",
  pathPrefix: "/timetable",
  title: "Add a lesson on Monday",
  body: "You placed the lesson on another day. For this tour, tap Monday and add a Maths lesson so we can walk through a teaching day together.",
  placement: "bottom",
  unnumbered: true,
};

const TIMETABLE_DRAG_MONDAY: OnboardingStep = {
  id: "timetable-drag-monday",
  targetId: "timetable-maths-chip",
  extraTargetIds: ["timetable-monday"],
  pathPrefix: "/timetable",
  title: "Drag Maths onto the timetable",
  body: "Drag Maths from the drawer onto Monday on the timetable.",
  placement: "bottom",
};

const TIMETABLE_DRAG_MONDAY_REMINDER: OnboardingStep = {
  id: "timetable-drag-monday-reminder",
  targetId: "timetable-maths-chip",
  extraTargetIds: ["timetable-monday"],
  pathPrefix: "/timetable",
  title: "Drag onto Monday",
  body: "You placed the lesson on another day. For this tour, drag Maths onto Monday so we can walk through a teaching day together.",
  placement: "bottom",
  unnumbered: true,
};

const TIMETABLE_CLICK_LESSON: OnboardingStep = {
  id: "timetable-click-lesson",
  targetId: "timetable-monday-lesson",
  pathPrefix: "/timetable",
  title: "Plan the lesson",
  body: "Click this lesson on the timetable to open the lesson plan.",
  placement: "bottom",
};

const SHARED_STEPS: OnboardingStep[] = [
  {
    id: "scheme-add-title",
    targetId: "scheme-title",
    pathPrefix: "/scheme",
    title: "Give it a title",
    body: "Type a short title for this lesson. Press Enter or click away when done.",
    placement: "bottom",
  },
  {
    id: "scheme-add-prep",
    targetId: "scheme-add-prep",
    pathPrefix: "/scheme",
    title: "Add a prep task",
    body: "Click Add prep task, write something you need to prepare, for example 'Print worksheet' then press Enter or click away when done.",
    placement: "top",
  },
  {
    id: "scheme-nav-teach",
    targetId: "nav-teach",
    pathPrefix: "/scheme",
    title: "Go to the Teach view",
    body: "You'll see how this helps you manage your teaching day.",
    placement: "bottom",
  },
  {
    id: "teach-intro",
    targetId: "teach-day-timetable",
    extraTargetIds: ["teach-day-heading"],
    pathPrefix: "/teach",
    title: "Your teaching day",
    body: "On a teaching day you come here to see what’s scheduled, what prep tasks need doing and work through the lessons. On a phone, use the arrows above and below the timetable to move through the day.",
    showNext: true,
    mobilePlacement: "top",
    desktopPlacement: "right-start",
  },
  {
    id: "teach-highlight-panels",
    targetId: "teach-attention-panels",
    pathPrefix: "/teach",
    title: "What still needs attention",
    body: "Unplanned lessons and prep tasks for the day show up here so nothing gets missed.",
    showNext: true,
    placement: "bottom",
  },
  {
    id: "teach-tick-prep",
    targetId: "teach-prep-section",
    pathPrefix: "/teach",
    title: "Tick off your prep task",
    body: "Mark your prep task as done to hide the warning.",
    placement: "bottom",
  },
  {
    id: "teach-complete-lesson",
    targetId: "teach-complete-checkbox",
    pathPrefix: "/teach",
    title: "Tick 'Planning Complete'",
    body: "This stops the lesson from showing up as unplanned on this screen.",
    placement: "bottom",
  },
  {
    id: "teach-all-set",
    targetId: "teach-day-heading",
    extraTargetIds: ["teach-day-timetable"],
    pathPrefix: "/teach",
    title: "🎉 That's it — you're all ready to plan your homeschool!",
    body: "You’ve scheduled a lesson, planned it, and worked through a teaching day.\n\nOther things to try now:",
    showNext: true,
    fullOverlay: true,
    bullets: [
      {
        emoji: "🔍",
        before: "Go to ",
        emphasis: "Calendar",
        after: " to manage term dates and holidays",
      },
      {
        emoji: "🔍",
        before: "Go to ",
        emphasis: "Timetable",
        after: " to schedule more lessons and manage topics",
      },
      {
        emoji: "🔍",
        before: "Click any lesson on the ",
        emphasis: "Timetable",
        after: " to plan or edit it",
      },
    ],
  },
];

export function onboardingStepsForDevice(mobile: boolean): OnboardingStep[] {
  const timetable = mobile
    ? [TIMETABLE_TAP_MONDAY, TIMETABLE_TAP_MONDAY_REMINDER, TIMETABLE_CLICK_LESSON]
    : [TIMETABLE_DRAG_MONDAY, TIMETABLE_DRAG_MONDAY_REMINDER, TIMETABLE_CLICK_LESSON];
  return [...timetable, ...SHARED_STEPS];
}

/** @deprecated Use onboardingStepsForDevice instead */
export const ONBOARDING_STEPS = onboardingStepsForDevice(false);

export const ONBOARDING_STORAGE_KEY = "schoolhappy_onboarding";

export type OnboardingPersisted = {
  status: "active" | "done" | "skipped";
  stepId: OnboardingStepId;
  /** ISO date of the Monday (or day) they placed the first lesson on */
  focusDate?: string | null;
  /** Device mode when the tour started (for step list and resume). */
  mobile?: boolean;
};
